const _crowdApiHost = (import.meta.env['VITE_CROWD_API_URL'] as string | undefined) ?? 'http://localhost:3011';
const _borderApiHost = import.meta.env['VITE_BORDER_API_URL'] as string | undefined;
const _criticalApiHost = import.meta.env['VITE_CRITICAL_API_URL'] as string | undefined;
const _forensicsApiHost = import.meta.env['VITE_FORENSICS_API_URL'] as string | undefined;
const _intelApiHost = import.meta.env['VITE_INTEL_API_URL'] as string | undefined;
const _gatewayApiHost = import.meta.env['VITE_API_GATEWAY_URL'] as string | undefined;
const _legacyCoreApiHost = import.meta.env['VITE_CORE_API_URL'] as string | undefined;

function apiBaseUrl(raw?: string, options: { preserveLocalhost?: boolean } = {}): string {
  if (!raw) return '/api/v1';

  const trimmed = raw.replace(/\/+$/, '');
  try {
    const url = new URL(trimmed);
    const isLocal = url.hostname === 'localhost'
      || url.hostname === '127.0.0.1'
      || url.hostname === '0.0.0.0';
    if (isLocal && !options.preserveLocalhost) return '/api/v1';
  } catch {
    if (trimmed === '/api' || trimmed === '/api/v1') {
      return trimmed === '/api' ? '/api/v1' : trimmed;
    }
  }

  if (trimmed.endsWith('/api/v1')) return trimmed;
  if (trimmed.endsWith('/api')) return `${trimmed}/v1`;
  return `${trimmed}/api/v1`;
}

export const API_BASE_URL = apiBaseUrl(_gatewayApiHost ?? _legacyCoreApiHost);
export const CROWD_API_BASE_URL = apiBaseUrl(_crowdApiHost, { preserveLocalhost: true });
export const BORDER_API_BASE_URL = apiBaseUrl(_borderApiHost, { preserveLocalhost: true });
export const CRITICAL_API_BASE_URL = apiBaseUrl(_criticalApiHost, { preserveLocalhost: true });
export const FORENSICS_API_BASE_URL = apiBaseUrl(_forensicsApiHost);
export const INTEL_API_BASE_URL = apiBaseUrl(_intelApiHost, { preserveLocalhost: true });

const CROWD_API_ROUTES = [
  '/alerts',
  '/command-reporting',
  '/crowd',
  '/events',
  '/geo-monitoring',
  '/geo/',
  '/operations',
  '/reporting/reports',
  '/reports',
  '/live-zone-metrics',
  '/teams',
  '/telemetry',
  '/venues',
  '/zones',
];

const CORE_BORDER_ROUTES = [
  '/admin/border/audit',
  '/border',
];

const CORE_BORDER_REPORT_ROUTES = [
  '/reporting/reports',
  '/reports',
];

function matchesExactRouteOrChild(url: string, route: string): boolean {
  return url === route || url.startsWith(`${route}/`) || url.startsWith(`${route}?`);
}

function baseUrlFor(url: string): string {
  const appNamespace = getAppNamespace();

  if (CORE_BORDER_ROUTES.some(route => matchesExactRouteOrChild(url, route))) {
    return API_BASE_URL;
  }
  if (appNamespace === 'border' && CORE_BORDER_REPORT_ROUTES.some(route => matchesExactRouteOrChild(url, route))) {
    return API_BASE_URL;
  }
  if (appNamespace === 'border' && matchesExactRouteOrChild(url, '/alerts')) {
    return API_BASE_URL;
  }
  if (_borderApiHost && (url === '/border' || url.startsWith('/border/'))) {
    return BORDER_API_BASE_URL;
  }
  if (_criticalApiHost && (url === '/critical-infra' || url.startsWith('/critical-infra/'))) {
    return CRITICAL_API_BASE_URL;
  }
  if (_forensicsApiHost && (url === '/forensics' || url.startsWith('/forensics/'))) {
    return FORENSICS_API_BASE_URL;
  }
  if (_intelApiHost && (url === '/intel' || url.startsWith('/intel/'))) {
    return INTEL_API_BASE_URL;
  }
  if (_intelApiHost && (url === '/watchlists' || url.startsWith('/watchlists/'))) {
    return INTEL_API_BASE_URL;
  }
  if (
    appNamespace !== 'crowd' &&
    (url === '/alerts' ||
      url.startsWith('/alerts/') ||
      url === '/zones' ||
      url.startsWith('/zones/') ||
      url.startsWith('/geo/'))
  ) {
    return API_BASE_URL;
  }
  if (_crowdApiHost && CROWD_API_ROUTES.some(route => url === route || url.startsWith(route))) {
    return CROWD_API_BASE_URL;
  }
  return API_BASE_URL;
}

function getAppNamespace(): string {
  const path = window.location.pathname;
  if (path.startsWith('/border')) return 'border';
  if (path.startsWith('/critical-infra') || path.startsWith('/ci')) return 'ci';
  if (path.startsWith('/forensics')) return 'forensics';
  if (path.startsWith('/intel')) return 'intel';
  if (path.startsWith('/login')) {
    const params = new URLSearchParams(window.location.search);
    const app = params.get('app');
    if (app === 'border') return 'border';
    if (app === 'ci') return 'ci';
    if (app === 'forensics') return 'forensics';
    if (app === 'intel') return 'intel';
  }
  return 'crowd';
}

export const TOKEN_KEY = (): string =>
  `vigilo_access_token_${getAppNamespace()}`;
export const USER_KEY = (): string =>
  `vigilo_user_${getAppNamespace()}`;
export const SESSION_ID_KEY = (): string =>
  `vigilo_session_id_${getAppNamespace()}`;
export const AUTH_CLEARED_EVENT = 'vigilo-auth-cleared';

const APP_NAMESPACES = ['crowd', 'border', 'ci', 'forensics', 'intel'];

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const pendingApiRequests = new Set<AbortController>();
let apiRequestsBlockedUntil = 0;
let apiRequestsBlockedReason = 'API requests are temporarily blocked';

export function abortPendingApiRequests(reason = 'Authentication state changed', blockForMs = 0): void {
  for (const controller of pendingApiRequests) {
    controller.abort(reason);
  }
  pendingApiRequests.clear();
  if (blockForMs > 0) {
    apiRequestsBlockedUntil = Date.now() + blockForMs;
    apiRequestsBlockedReason = reason;
  }
}

export function clearAuthStorage(): void {
  const keys = new Set<string>([
    TOKEN_KEY(),
    USER_KEY(),
    'vigilo_access_token',
    'vigilo_user',
  ]);
  for (const namespace of APP_NAMESPACES) {
    keys.add(`vigilo_access_token_${namespace}`);
    keys.add(`vigilo_user_${namespace}`);
  }
  for (const key of keys) localStorage.removeItem(key);
  sessionStorage.clear();
  window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY());
  return {
    'Content-Type': 'application/json',
    'X-Vigilo-Session-Id': sessionId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function sessionId(): string {
  const key = SESSION_ID_KEY();
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const generated = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(key, generated);
  return generated;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    // Only force logout if token is missing or the request was to
    // an auth endpoint. A 401 from a feature endpoint (wrong role,
    // insufficient permissions) should NOT destroy the session.
    const token = localStorage.getItem(TOKEN_KEY());
    const isAuthEndpoint = res.url.includes('/auth/') ||
      res.url.includes('/login');

    if (!token || isAuthEndpoint) {
      // Token genuinely missing or auth endpoint rejected — logout
      clearAuthStorage();
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    } else {
      // Token exists but this endpoint returned 401 (role/permission
      // issue or transient error) — throw without destroying session
      console.warn(
        `[API] 401 on ${res.url} — permission denied or token issue.` +
        ' Session preserved. Check role permissions.'
      );
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let body: unknown = text;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = text;
      }
    }
    let detail = '';
    if (body && typeof body === 'object') {
      const errorBody = body as { message?: unknown; error?: unknown; status?: unknown };
      detail = String(errorBody.message ?? errorBody.error ?? errorBody.status ?? '');
    }
    throw new ApiError(detail ? `HTTP ${res.status}: ${detail}` : `HTTP ${res.status}`, res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function trackedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const url = String(input);
  const path = url.startsWith('http') ? new URL(url).pathname.replace(/^\/api\/v1/, '') : url;
  if (Date.now() < apiRequestsBlockedUntil && !path.startsWith('/auth/')) {
    return Promise.reject(new DOMException(apiRequestsBlockedReason, 'AbortError'));
  }
  if (init.signal) return fetch(input, init);
  const controller = new AbortController();
  pendingApiRequests.add(controller);
  return fetch(input, { ...init, signal: controller.signal })
    .finally(() => pendingApiRequests.delete(controller));
}

export function get<T>(url: string): Promise<T> {
  return trackedFetch(`${baseUrlFor(url)}${url}`, { headers: authHeaders() })
    .then(res => handleResponse<T>(res));
}

export function getCore<T>(url: string): Promise<T> {
  return trackedFetch(`${API_BASE_URL}${url}`, { headers: authHeaders() })
    .then(res => handleResponse<T>(res));
}

export function post<T>(url: string, body?: unknown): Promise<T> {
  return trackedFetch(`${baseUrlFor(url)}${url}`, {
    method: 'POST',
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(res => handleResponse<T>(res));
}

export function patch<T>(url: string, body?: unknown): Promise<T> {
  return trackedFetch(`${baseUrlFor(url)}${url}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(res => handleResponse<T>(res));
}

export function put<T>(url: string, body?: unknown): Promise<T> {
  return trackedFetch(`${baseUrlFor(url)}${url}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(res => handleResponse<T>(res));
}

export function del<T = void>(url: string, body?: unknown): Promise<T> {
  return trackedFetch(`${baseUrlFor(url)}${url}`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(res => handleResponse<T>(res));
}

export function unwrapArray<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  if (res && typeof res === 'object') {
    const r = res as Record<string, unknown>;
    if (Array.isArray(r.data)) return r.data as T[];
    if (Array.isArray(r.items)) return r.items as T[];
  }
  return [];
}

export function unwrapInterdictions<T>(res: unknown): T[] {
  return unwrapArray<T>(res);
}

export type ApiList<T> = T[] | { data?: T[] };

export function unwrapList<T>(body: ApiList<T> | unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const wrapped = body as { data?: T[] };
  return Array.isArray(wrapped?.data) ? wrapped.data : [];
}
